import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  FormControlLabel,
  IconButton,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useForm, useController } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { AutocompleteFormInput, TextFieldFormInput } from '../formInputs';
import StyledButton from '../buttons/StyledButton';
import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent, type DragEvent } from 'react';
import { useAlert, useCurrentUser, useDatastore } from '../../hooks';
import { usePackageTypes } from '../../hooks/usePackageType';
import {
  QM_INTEGRATION_ONLY_REASON_CODES,
  QM_MAX_ATTACHMENTS,
  QM_MAX_ATTACHMENT_SIZE_BYTES
} from '../../common/constants';
import { QMIssueProps } from '../../datastore/qmIssue';
import { QMReasonProps } from '../../datastore/qmReason';
import { fetchPODetails } from '../../api/poApi';
import { ReceiptLine } from '../../common/types';
import useObserveSsccs from '../../hooks/useSubscribeSsccs';
import { SSCCProps } from '../../datastore/ssccs';
import { QmRecordType } from '../../api/qmCrud';
import {
  QmClosureCodeOption,
  listQmReferenceClosureCodesRequest,
  listQmReferenceLocationsRequest,
  listQmSiteInboundLocationsRequest,
  listQmReferenceReasonsRequest
} from '../../api/qmCrud';
import { ATTACHMENT_ACCEPT_TYPES, isSupportedAttachment, normalizeAttachmentFiles } from '../../common/qmAttachments';

interface RaiseQMNewFormProps {
  open: boolean;
  onSubmit: (data: RaiseQMNewModel) => void | Promise<void>;
  onClose: () => void;
  type?: QmRecordType;
  allowedReasonCodes?: string[];
  initialSsccOrAsn?: string;
  initialPurchaseOrderNumber?: string;
  siteInboundManifestContext?: QmSiteInboundManifestContext;
}

export type QmSiteInboundAsnLine = {
  asn: string;
  poLineId: string;
  itemId: string;
  sourcePartId?: string;
  description?: string;
  quantityOrdered?: number | null;
  quantityReceived?: number | null;
};

export type QmSiteInboundAsnDetail = {
  asn: string;
  lines: QmSiteInboundAsnLine[];
};

export type QmSiteInboundManifestContext = {
  targetScope?: 'sscc' | 'asn';
  tripId?: string;
  tripReference?: string;
  manifestId?: string;
  receivingWarehouseId?: string | number | null;
  receivingWarehouseIds?: Array<string | number>;
  contactEmail?: string;
  packageCount?: number | null;
  packageTypes?: string;
  packageConfigurations?: QmPackageConfiguration[];
  asnDetails?: QmSiteInboundAsnDetail[];
};

type PoLineOption = {
  name: string;
  value: string;
  asn?: string;
  poLineId: string;
  itemId: string;
  status: string;
  sourcePartId: string;
  purchaseType: string;
  description: string;
  quantityOrdered: number;
  quantityReceived: number;
};

export type QmPoLineSelection = {
  lineIndex: number;
  poLineId: string;
  poLine: string;
  itemId: string;
  linePayload: Record<string, unknown> | null;
  orderedQuantity: number | null;
  receivedQuantity: number | null;
  quantityVariance: number | null;
};

export type QmPackageConfiguration = {
  packageNumber: number;
  packageType: string;
};

export interface RaiseQMNewModel {
  type: QmRecordType;
  reason: QMReasonProps;
  issue: QMIssueProps;
  ssccOrAsn: string;
  purchaseOrderNumber: string;
  purchaseOrderLine: string;
  poLine: string;
  poLineId: string;
  itemId: string;
  supplierCode: string;
  supplierName: string;
  contactName: string;
  other: string;
  numberOfLabels: number | '';
  packageTypes: string;
  printLabels: boolean;
  locationName: string;
  itemLocationId?: number | null;
  itemLocationLabel?: string | null;
  progress: string;
  description: string;
  closureCode: string;
  closureNotes: string;
  attachments: File[];
  poLineSelections: QmPoLineSelection[];
  packageConfigurations: QmPackageConfiguration[];
}

const REASONS_ALLOW_MISSING_IDENTIFIER = new Set(['MISSING_PAPERWORK', 'INCORRECT_PO_TO', 'INCORRECT_DELIVERY']);
const INTEGRATION_ONLY_REASON_CODES = new Set(QM_INTEGRATION_ONLY_REASON_CODES);
const SITE_INBOUND_SSCC_OPTIONAL_PACKAGE_REASON_CODES = new Set([
  'SITE_INBOUND_SSCC_NOT_ON_MANIFEST',
  'SITE_INBOUND_SSCC_NOT_DELIVERED',
  'SITE_INBOUND_DAMAGED_IN_TRANSIT',
  'SITE_INBOUND_NOT_FIT_FOR_TRANSPORT'
]);
const SITE_INBOUND_ATTACHMENT_REQUIRED_REASON_CODES = new Set([
  'SITE_INBOUND_SSCC_NOT_ON_MANIFEST',
  'SITE_INBOUND_DAMAGED_IN_TRANSIT',
  'SITE_INBOUND_NOT_FIT_FOR_TRANSPORT',
  'SITE_INBOUND_ASN_NOT_ON_MANIFEST',
  'SITE_INBOUND_INCORRECT_GOODS',
  'SITE_INBOUND_INCORRECT_QTY'
]);
const SITE_INBOUND_PRINT_LABEL_DEFAULTS_BY_REASON: Record<string, boolean> = {
  SITE_INBOUND_SSCC_NOT_ON_MANIFEST: true,
  SITE_INBOUND_SSCC_NOT_DELIVERED: false,
  SITE_INBOUND_DAMAGED_IN_TRANSIT: true,
  SITE_INBOUND_NOT_FIT_FOR_TRANSPORT: false,
  SITE_INBOUND_ASN_NOT_ON_MANIFEST: true,
  SITE_INBOUND_ASN_NOT_DELIVERED: false,
  SITE_INBOUND_INCORRECT_GOODS: true,
  SITE_INBOUND_INCORRECT_QTY: true
};
const SITE_INBOUND_LOCK_ALL_ASN_REASON_CODES = new Set([
  'SITE_INBOUND_SSCC_NOT_ON_MANIFEST',
  'SITE_INBOUND_SSCC_NOT_DELIVERED',
  'SITE_INBOUND_DAMAGED_IN_TRANSIT',
  'SITE_INBOUND_NOT_FIT_FOR_TRANSPORT'
]);
const SITE_INBOUND_DEFAULT_SELECT_ASN_REASON_CODES = new Set<string>([]);
const SITE_INBOUND_ASN_LINE_REQUIRED_REASON_CODES = new Set([
  'SITE_INBOUND_ASN_NOT_ON_MANIFEST',
  'SITE_INBOUND_ASN_NOT_DELIVERED',
  'SITE_INBOUND_INCORRECT_GOODS',
  'SITE_INBOUND_INCORRECT_QTY'
]);

type AttachmentPreview = {
  id: string;
  name: string;
  url: string | null;
  file: File;
  isImage: boolean;
};

function formatBytes(size: number): string {
  return size < 1024 * 1024 ? `${(size / 1024).toFixed(1)} KB` : `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function PackageCountStepper({ control }: { control: ReturnType<typeof useForm<RaiseQMNewModel>>['control'] }) {
  const {
    field: { value, onChange },
    fieldState: { error }
  } = useController({
    name: 'numberOfLabels',
    control,
    rules: {
      validate: (v) => {
        if (!v && v !== 0) return 'Enter Number of Packages';
        return Number(v) >= 1 || 'Must be at least 1';
      }
    }
  });
  const count = Math.max(1, Number(value) || 1);
  const displayValue = !value && value !== 0 ? '' : Number(value);

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="center" sx={{ width: 'fit-content' }}>
        <IconButton
          size="small"
          onClick={() => onChange(String(Math.max(1, count - 1)))}
          disabled={count <= 1}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRight: 'none',
            borderRadius: '4px 0 0 4px',
            p: 0.75
          }}>
          <RemoveIcon fontSize="small" />
        </IconButton>
        <OutlinedInput
          size="small"
          value={displayValue}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onChange('');
            } else {
              const v = parseInt(raw);
              onChange(isNaN(v) ? '' : String(v));
            }
          }}
          inputProps={{ style: { textAlign: 'center', width: 48, padding: '0.45em 0', fontWeight: 600 } }}
          sx={{
            borderRadius: 0
          }}
        />
        <IconButton
          size="small"
          onClick={() => {
            if (!value && value !== 0) {
              onChange('1');
              return;
            }
            onChange(String(count + 1));
          }}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: 'none',
            borderRadius: '0 4px 4px 0',
            p: 0.75
          }}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
      {error && (
        <Typography variant="caption" color="error" sx={{ fontSize: '0.68rem' }}>
          {error.message || 'Required'}
        </Typography>
      )}
    </Stack>
  );
}

export default function RaiseQMNewForm({
  open,
  onSubmit,
  onClose,
  type = 'INBOUND',
  allowedReasonCodes,
  initialSsccOrAsn = '',
  initialPurchaseOrderNumber = '',
  siteInboundManifestContext
}: RaiseQMNewFormProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const alert = useAlert();
  const store = useDatastore();
  const packageTypeOptions = usePackageTypes();
  const packageTypeSelectOptions = useMemo(
    () => (packageTypeOptions && packageTypeOptions.length > 0 ? packageTypeOptions : ['Carton']),
    [packageTypeOptions]
  );
  const user = useCurrentUser();
  const ssccs = useObserveSsccs(0);
  const [qmReasons, setQMReasons] = useState<QMReasonProps[]>([]);
  const [qmIssues, setQMIssues] = useState<QMIssueProps[]>([]);
  const [qmClosureCodes, setQmClosureCodes] = useState<QmClosureCodeOption[]>([]);
  const [qmLocations, setQmLocations] = useState<
    Array<{
      code: string;
      label: string;
      value: string;
      itemLocationId?: number | null;
      receivingWarehouseId?: string | number | null;
      receivingWarehouseLabel?: string | null;
    }>
  >([]);
  const [isReasonOptionsLoading, setIsReasonOptionsLoading] = useState(true);
  const [isClosureOptionsLoading, setIsClosureOptionsLoading] = useState(true);
  const [isLocationOptionsLoading, setIsLocationOptionsLoading] = useState(true);
  const [isPoSearchLoading, setIsPoSearchLoading] = useState(false);
  const [reasonDisplayOrderByCode, setReasonDisplayOrderByCode] = useState<Record<string, number>>({});
  const [ssccOrAsnInput, setSsccOrAsnInput] = useState('');
  const [purchaseOrderInput, setPurchaseOrderInput] = useState('');
  const [purchaseOrderLineOptions, setPurchaseOrderLineOptions] = useState<PoLineOption[]>([]);
  const [selectedPoLineOptions, setSelectedPoLineOptions] = useState<PoLineOption[]>([]);
  const [selectedSiteInboundAsns, setSelectedSiteInboundAsns] = useState<string[]>([]);
  const [packageConfigurations, setPackageConfigurations] = useState<QmPackageConfiguration[]>([]);
  const [attachmentPreviews, setAttachmentPreviews] = useState<AttachmentPreview[]>([]);
  const [isAttachmentDragActive, setIsAttachmentDragActive] = useState(false);
  const attachmentDragDepthRef = useRef(0);
  const imagePreviewUrlsRef = useRef<string[]>([]);
  const [selectedPreview, setSelectedPreview] = useState<{ name: string; url: string } | null>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);
  const isSubmitInProgressRef = useRef(false);
  const isSiteInboundManifest = type === 'SITE_INBOUND' && Boolean(siteInboundManifestContext);
  const defaultContactEmail = String(siteInboundManifestContext?.contactEmail || user.email || '').trim();
  const defaultPackageCount = siteInboundManifestContext?.packageCount ?? '';
  const siteInboundReceivingWarehouseIds = useMemo(() => {
    const values = [
      ...(siteInboundManifestContext?.receivingWarehouseIds || []),
      ...(siteInboundManifestContext?.receivingWarehouseId != null
        ? [siteInboundManifestContext.receivingWarehouseId]
        : [])
    ]
      .map((warehouseId) => String(warehouseId || '').trim())
      .filter((warehouseId) => !!warehouseId);

    return Array.from(new Set(values));
  }, [siteInboundManifestContext?.receivingWarehouseId, siteInboundManifestContext?.receivingWarehouseIds]);
  const defaultPackageConfigurations = useMemo(
    () => siteInboundManifestContext?.packageConfigurations ?? [],
    [siteInboundManifestContext?.packageConfigurations]
  );

  const sectionCardSx = {
    p: { xs: 1.25, md: 1.5 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper'
  };

  const sectionTitleSx = {
    mb: 1,
    fontSize: '0.82rem',
    fontWeight: 600,
    color: 'text.secondary'
  };

  const fieldLabelSx = {
    fontWeight: 500,
    color: 'text.primary',
    lineHeight: 1.25
  };

  const renderFieldLabel = (label: string, required = false) => (
    <Typography sx={fieldLabelSx}>
      {label}
      {required ? (
        <Box component="span" sx={{ color: 'error.main', ml: 0.35, fontWeight: 700 }}>
          *
        </Box>
      ) : null}
    </Typography>
  );

  const invalidOutlineSx = (isInvalid: boolean) => ({
    borderRadius: 1.5,
    p: 0.5,
    transition: 'box-shadow 0.2s ease',
    ...(isInvalid
      ? {
          boxShadow: `0 0 0 1px ${theme.palette.error.main}`
        }
      : {})
  });

  function formatStatusLabel(rawStatus: string): string {
    const normalized = rawStatus.trim().toLowerCase();

    if (normalized === 'partially_received' || normalized === 'partially-received') {
      return 'Partially Received';
    }

    return rawStatus.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }

  const normalizePackageTypeToKnownOption = useCallback(
    (value?: string | null) => {
      const trimmed = String(value || '').trim();
      if (!trimmed) return '';

      const exactMatch = packageTypeSelectOptions.find((option) => option === trimmed);
      if (exactMatch) return exactMatch;

      const normalized = trimmed.toUpperCase();
      return packageTypeSelectOptions.find((option) => option.trim().toUpperCase() === normalized) || trimmed;
    },
    [packageTypeSelectOptions]
  );

  const normalizePackageTypeListToKnownOptions = useCallback(
    (value?: string | null) =>
      String(value || '')
        .split(',')
        .map((entry) => normalizePackageTypeToKnownOption(entry))
        .filter((entry) => !!entry)
        .join(', '),
    [normalizePackageTypeToKnownOption]
  );

  function buildPoLineOption(line: ReceiptLine): PoLineOption | null {
    const poLineNum = String(line.poLineNum || '').trim();
    if (!poLineNum) return null;
    const resolvedPoLineId = String(line.originalPoLineId ?? line.id ?? '').trim();
    const resolvedItemId =
      [line.partId, line.itemId].map((value) => String(value ?? '').trim()).find((value) => value.length > 0) || '';
    if (!resolvedPoLineId) return null;

    const status = formatStatusLabel(String(line.status || 'N/A').trim());
    const sourcePartId = String(line.sourcePartId || 'N/A').trim();
    const purchaseType = String(line.purchaseType || 'N/A').trim();
    const quantityOrdered = line.quantityOrdered ?? 0;
    const quantityReceived = line.quantityReceived ?? 0;
    const description = String(line.description || 'N/A').trim();
    const generatedPoLineText = `PO Line ${poLineNum} ${sourcePartId} ${purchaseType} ${description}`
      .replace(/\s+/g, ' ')
      .trim();

    return {
      value: generatedPoLineText,
      name: `PO Line ${poLineNum}`,
      poLineId: resolvedPoLineId,
      itemId: resolvedItemId,
      status,
      sourcePartId,
      purchaseType,
      description: description,
      quantityOrdered,
      quantityReceived
    };
  }

  const extractPoLineToken = useCallback((rawValue: string): string => {
    const normalized = String(rawValue || '').trim();
    if (!normalized) return '';

    const poLinePrefixMatch = normalized.match(/^PO\s+Line\s+(.+)$/i);
    if (poLinePrefixMatch?.[1]) {
      return poLinePrefixMatch[1].trim().split(/\s+/)[0] || '';
    }

    return normalized;
  }, []);

  const resolvePoLineOption = useCallback(
    (rawValue: string, fallbackPoLineId?: string): PoLineOption | null => {
      const normalizedValue = String(rawValue || '').trim();
      const normalizedPoLineId = String(fallbackPoLineId || '').trim();

      if (!normalizedValue && !normalizedPoLineId) return null;

      if (normalizedPoLineId) {
        const byId = purchaseOrderLineOptions.find((option) => option.poLineId === normalizedPoLineId);
        if (byId) return byId;
      }

      const directMatch = purchaseOrderLineOptions.find(
        (option) => option.value === normalizedValue || option.name === normalizedValue
      );
      if (directMatch) return directMatch;

      const token = extractPoLineToken(normalizedValue);
      if (!token) return null;

      return (
        purchaseOrderLineOptions.find((option) => {
          const optionToken = extractPoLineToken(option.name);
          return optionToken.toLowerCase() === token.toLowerCase();
        }) || null
      );
    },
    [extractPoLineToken, purchaseOrderLineOptions]
  );

  const defaultValues: RaiseQMNewModel = {
    type,
    reason: { id: '', reasonId: '', name: '' },
    issue: { id: '', issueId: '', name: '' },
    ssccOrAsn: initialSsccOrAsn,
    purchaseOrderNumber: initialPurchaseOrderNumber,
    purchaseOrderLine: '',
    poLine: '',
    poLineId: '',
    itemId: '',
    supplierCode: '',
    supplierName: '',
    contactName: '',
    other: '',
    numberOfLabels: '',
    packageTypes: '',
    printLabels: !isSiteInboundManifest,
    locationName: '',
    progress: '',
    description: '',
    closureCode: '',
    closureNotes: '',
    attachments: [],
    poLineSelections: [],
    packageConfigurations: []
  };

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors }
  } = useForm<RaiseQMNewModel>({ defaultValues });

  const currentSupplierName = String(watch('supplierName') || '').trim();
  const currentContactName = String(watch('contactName') || '').trim();
  const currentPackageTypes = String(watch('packageTypes') || '').trim();
  const currentNumberOfLabels = watch('numberOfLabels');
  const currentPrintLabels = Boolean(watch('printLabels'));
  const currentSsccOrAsn = String(watch('ssccOrAsn') || '').trim();
  const currentPurchaseOrderNumber = String(watch('purchaseOrderNumber') || '').trim();
  const selectedReasonCode = String(watch('reason.reasonId') || '').trim();
  const selectedClosureCode = String(watch('closureCode') || '').trim();
  const selectedPoLine = String(watch('purchaseOrderLine') || '').trim();
  const normalizedPackageCount = Number(currentNumberOfLabels || 0);
  const isMultiPackageConfig = Number.isFinite(normalizedPackageCount) && normalizedPackageCount > 1;
  const selectedLocationName = String(watch('locationName') || '').trim();
  const isPoProvided = currentPurchaseOrderNumber.length > 0;
  const isSsccOrAsnProvided = currentSsccOrAsn.length > 0;
  const hasSelectedReason = selectedReasonCode.length > 0;
  const allowsMissingIdentifier = REASONS_ALLOW_MISSING_IDENTIFIER.has(selectedReasonCode);
  const requiresIdentifier = hasSelectedReason && !allowsMissingIdentifier;
  const isSsccOrAsnRequired = requiresIdentifier && !isPoProvided;
  const isPurchaseOrderRequired = !isSiteInboundManifest && requiresIdentifier && !isSsccOrAsnProvided;
  const hasSupplierOrContact = !!currentSupplierName || !!currentContactName;
  const requiresSupplierOrContact =
    hasSelectedReason && (allowsMissingIdentifier || (!isPoProvided && !isSsccOrAsnProvided));

  const isSupplierRequired = requiresSupplierOrContact && !currentContactName;
  const isContactRequired = requiresSupplierOrContact && !currentSupplierName;
  const shouldLockSiteInboundAsnLines =
    isSiteInboundManifest &&
    siteInboundManifestContext?.targetScope === 'sscc' &&
    SITE_INBOUND_LOCK_ALL_ASN_REASON_CODES.has(selectedReasonCode);
  const shouldDefaultSelectSiteInboundAsnLines =
    isSiteInboundManifest &&
    siteInboundManifestContext?.targetScope === 'sscc' &&
    SITE_INBOUND_DEFAULT_SELECT_ASN_REASON_CODES.has(selectedReasonCode);
  const selectableSiteInboundAsnDetails = useMemo(() => {
    if (!isSiteInboundManifest || !siteInboundManifestContext?.asnDetails?.length) return [];
    return siteInboundManifestContext.asnDetails.filter((asnDetail) => String(asnDetail.asn || '').trim());
  }, [isSiteInboundManifest, siteInboundManifestContext?.asnDetails]);

  const selectedReasonDisplayOrder = reasonDisplayOrderByCode[selectedReasonCode] ?? null;
  const shouldShowClosureSection = selectedReasonDisplayOrder !== null && selectedReasonDisplayOrder <= 4;
  const isClosureSelectionRequired = selectedReasonDisplayOrder !== null && selectedReasonDisplayOrder <= 2;
  const hasClosureOptionsForReason = qmClosureCodes.length > 0;
  const isSiteInboundOptionalPackageReason =
    isSiteInboundManifest && SITE_INBOUND_SSCC_OPTIONAL_PACKAGE_REASON_CODES.has(selectedReasonCode);
  const isAttachmentRequired =
    isSiteInboundManifest && SITE_INBOUND_ATTACHMENT_REQUIRED_REASON_CODES.has(selectedReasonCode);
  const isSiteInboundAsnLineRequired =
    isSiteInboundManifest && SITE_INBOUND_ASN_LINE_REQUIRED_REASON_CODES.has(selectedReasonCode);
  const hasSiteInboundLocationOptions = isSiteInboundManifest && qmLocations.length > 0;
  const isItemLocationRequired = !isSiteInboundManifest || isLocationOptionsLoading || hasSiteInboundLocationOptions;
  const siteInboundWarehouseDisplay = useMemo(() => {
    if (!isSiteInboundManifest) return '';
    const labels = Array.from(
      new Set(
        qmLocations
          .map((location) => String(location.receivingWarehouseLabel || location.receivingWarehouseId || '').trim())
          .filter((label) => !!label)
      )
    );
    if (labels.length > 0) return labels.join(', ');
    if (siteInboundReceivingWarehouseIds.length > 0) return siteInboundReceivingWarehouseIds.join(', ');
    return '';
  }, [isSiteInboundManifest, qmLocations, siteInboundReceivingWarehouseIds]);
  const siteInboundAsnLineOptions = useMemo<PoLineOption[]>(() => {
    if (!isSiteInboundManifest) return [];
    return (siteInboundManifestContext?.asnDetails || []).flatMap((asnDetail) =>
      asnDetail.lines.map((line) => ({
        name: `${asnDetail.asn} - ${line.sourcePartId || line.itemId}${line.description ? ` - ${line.description}` : ''}`,
        value: String(line.poLineId || line.sourcePartId || line.itemId || '').trim(),
        asn: String(asnDetail.asn || '').trim(),
        poLineId: String(line.poLineId || '').trim(),
        itemId: String(line.itemId || '').trim(),
        status: '',
        sourcePartId: String(line.sourcePartId || '').trim(),
        purchaseType: '',
        description: String(line.description || '').trim(),
        quantityOrdered: Number(line.quantityOrdered || line.quantityReceived || 0),
        quantityReceived: Number(line.quantityReceived || 0)
      }))
    );
  }, [isSiteInboundManifest, siteInboundManifestContext?.asnDetails]);

  const missingRequiredItems = useMemo(() => {
    const missing: string[] = [];

    if (!selectedReasonCode) missing.push('Reason For QM');
    if (requiresIdentifier && !currentSsccOrAsn && (!currentPurchaseOrderNumber || isSiteInboundManifest)) {
      missing.push(isSiteInboundManifest ? 'SSCC / ASN' : 'SSCC / ASN or Purchase Order');
    }
    if (requiresSupplierOrContact && !hasSupplierOrContact) {
      missing.push('Supplier or Contact');
    }
    if (!isSiteInboundOptionalPackageReason && !currentNumberOfLabels && currentNumberOfLabels !== 0) {
      missing.push('Number of Packages');
    }
    if (!isSiteInboundOptionalPackageReason && !isMultiPackageConfig && !currentPackageTypes) {
      missing.push('Package Types');
    }
    if (
      !isSiteInboundOptionalPackageReason &&
      isMultiPackageConfig &&
      packageConfigurations.some((pkg) => !String(pkg.packageType || '').trim())
    ) {
      missing.push('Package Types');
    }
    if (isItemLocationRequired && !selectedLocationName) missing.push('Item Location');
    if (!isSiteInboundManifest && isClosureSelectionRequired && hasClosureOptionsForReason && !selectedClosureCode) {
      missing.push('Closure Code');
    }
    if (shouldDefaultSelectSiteInboundAsnLines && selectedSiteInboundAsns.length === 0) {
      missing.push('ASN');
    }
    if (isSiteInboundAsnLineRequired && selectedPoLineOptions.length === 0) {
      missing.push('ASN Line');
    }
    if (isAttachmentRequired && attachmentPreviews.length === 0) missing.push('File Upload');

    return missing;
  }, [
    selectedReasonCode,
    requiresIdentifier,
    isSiteInboundManifest,
    requiresSupplierOrContact,
    hasSupplierOrContact,
    currentSsccOrAsn,
    currentPurchaseOrderNumber,
    currentNumberOfLabels,
    currentPackageTypes,
    isMultiPackageConfig,
    packageConfigurations,
    selectedLocationName,
    isItemLocationRequired,
    isClosureSelectionRequired,
    hasClosureOptionsForReason,
    selectedClosureCode,
    shouldDefaultSelectSiteInboundAsnLines,
    selectedSiteInboundAsns.length,
    selectedPoLineOptions.length,
    isSiteInboundOptionalPackageReason,
    isSiteInboundAsnLineRequired,
    isAttachmentRequired,
    attachmentPreviews.length
  ]);

  useEffect(() => {
    async function getData() {
      try {
        const siteInboundLocationPromise =
          isSiteInboundManifest && siteInboundReceivingWarehouseIds.length > 0
            ? Promise.allSettled(
                siteInboundReceivingWarehouseIds.map((receivingWarehouseId) =>
                  listQmSiteInboundLocationsRequest(receivingWarehouseId)
                )
              ).then((locationResults) => {
                const locationGroups = locationResults.flatMap((result) =>
                  result.status === 'fulfilled' ? result.value : []
                );

                return locationGroups.flat().map((location) => {
                  const warehouseLabel = String(
                    location.receivingWarehouseLabel || location.receivingWarehouseId || ''
                  ).trim();
                  return {
                    code: location.code,
                    label:
                      siteInboundReceivingWarehouseIds.length > 1 && warehouseLabel
                        ? `${location.label} - ${warehouseLabel}`
                        : location.label,
                    value: `${location.receivingWarehouseId}:${location.code}:${location.id}`,
                    itemLocationId: location.id,
                    receivingWarehouseId: location.receivingWarehouseId,
                    receivingWarehouseLabel: location.receivingWarehouseLabel
                  };
                });
              })
            : isSiteInboundManifest
              ? Promise.resolve([])
              : listQmReferenceLocationsRequest();
        const [reasons, locations] = await Promise.all([
          listQmReferenceReasonsRequest(type),
          siteInboundLocationPromise
        ]);
        const issues = await store.qmIssues.getArray();
        if (reasons) {
          const allowedReasonCodeSet = allowedReasonCodes ? new Set(allowedReasonCodes) : null;
          const filteredReasons = reasons.filter(
            (reason) =>
              !INTEGRATION_ONLY_REASON_CODES.has(reason.code) &&
              (!allowedReasonCodeSet || allowedReasonCodeSet.has(reason.code))
          );
          setReasonDisplayOrderByCode(Object.fromEntries(reasons.map((reason) => [reason.code, reason.displayOrder])));
          setQMReasons(
            filteredReasons.map((reason) => ({
              id: reason.code,
              reasonId: reason.code,
              name: reason.label
            }))
          );
        }
        if (locations) {
          setQmLocations(
            locations.map((location) => ({
              code: location.code,
              label: location.label,
              value: 'value' in location ? location.value : location.code,
              itemLocationId: 'itemLocationId' in location ? location.itemLocationId : null,
              receivingWarehouseId: 'receivingWarehouseId' in location ? location.receivingWarehouseId : null,
              receivingWarehouseLabel: 'receivingWarehouseLabel' in location ? location.receivingWarehouseLabel : null
            }))
          );
        }
        if (issues) setQMIssues(issues);
      } finally {
        setIsReasonOptionsLoading(false);
        setIsLocationOptionsLoading(false);
      }
    }
    getData();
  }, [store, type, allowedReasonCodes, isSiteInboundManifest, siteInboundReceivingWarehouseIds]);

  useEffect(() => {
    if (!open) return;
    setIsSubmitInProgress(false);
    setValue('type', type);
    setValue('ssccOrAsn', initialSsccOrAsn);
    setSsccOrAsnInput(initialSsccOrAsn);
    setValue('purchaseOrderNumber', isSiteInboundManifest ? '' : initialPurchaseOrderNumber);
    setPurchaseOrderInput(isSiteInboundManifest ? '' : initialPurchaseOrderNumber);
    setValue('contactName', defaultContactEmail);
    setValue('numberOfLabels', defaultPackageCount || '');
    setValue(
      'packageTypes',
      isSiteInboundManifest
        ? normalizePackageTypeListToKnownOptions(siteInboundManifestContext?.packageTypes)
        : siteInboundManifestContext?.packageTypes || ''
    );
    if (defaultPackageConfigurations.length) {
      const normalizedPackageConfigurations = isSiteInboundManifest
        ? defaultPackageConfigurations.map((pkg) => ({
            ...pkg,
            packageType: normalizePackageTypeToKnownOption(pkg.packageType)
          }))
        : defaultPackageConfigurations;
      setPackageConfigurations(normalizedPackageConfigurations);
      setValue('packageConfigurations', normalizedPackageConfigurations);
    }
    setValue('printLabels', !isSiteInboundManifest);
  }, [
    open,
    type,
    initialSsccOrAsn,
    initialPurchaseOrderNumber,
    isSiteInboundManifest,
    defaultContactEmail,
    defaultPackageCount,
    defaultPackageConfigurations,
    siteInboundManifestContext?.packageTypes,
    normalizePackageTypeListToKnownOptions,
    normalizePackageTypeToKnownOption,
    setValue
  ]);

  useEffect(() => {
    if (!open || !selectedReasonCode) return;
    const defaultPrintLabels = SITE_INBOUND_PRINT_LABEL_DEFAULTS_BY_REASON[selectedReasonCode];
    if (defaultPrintLabels === undefined) return;
    setValue('printLabels', defaultPrintLabels);
  }, [open, selectedReasonCode, setValue]);

  useEffect(() => {
    if (shouldLockSiteInboundAsnLines) {
      setSelectedSiteInboundAsns(selectableSiteInboundAsnDetails.map((asnDetail) => asnDetail.asn));
      setSelectedPoLineOptions(siteInboundAsnLineOptions);
      return;
    }

    if (shouldDefaultSelectSiteInboundAsnLines) {
      setSelectedSiteInboundAsns(selectableSiteInboundAsnDetails.map((asnDetail) => asnDetail.asn));
      return;
    }

    setSelectedSiteInboundAsns([]);
  }, [
    selectableSiteInboundAsnDetails,
    selectedReasonCode,
    shouldDefaultSelectSiteInboundAsnLines,
    shouldLockSiteInboundAsnLines,
    siteInboundAsnLineOptions
  ]);

  useEffect(() => {
    if (!shouldDefaultSelectSiteInboundAsnLines) return;
    const selectedAsnSet = new Set(selectedSiteInboundAsns.map((asn) => asn.trim().toUpperCase()));
    setSelectedPoLineOptions(
      siteInboundAsnLineOptions.filter((line) => line.asn && selectedAsnSet.has(line.asn.trim().toUpperCase()))
    );
  }, [selectedSiteInboundAsns, shouldDefaultSelectSiteInboundAsnLines, siteInboundAsnLineOptions]);

  useEffect(() => {
    let active = true;

    async function loadClosureCodes() {
      setIsClosureOptionsLoading(true);
      try {
        const closureCodes = await listQmReferenceClosureCodesRequest(selectedReasonCode || null);
        if (active) {
          setQmClosureCodes(closureCodes || []);
        }
      } finally {
        if (active) {
          setIsClosureOptionsLoading(false);
        }
      }
    }

    loadClosureCodes();

    return () => {
      active = false;
    };
  }, [selectedReasonCode]);

  useEffect(() => {
    if (!selectedPoLine) {
      setValue('poLineId', '');
      setValue('itemId', '');
      return;
    }

    const matchedPoLine = resolvePoLineOption(selectedPoLine);

    if (!matchedPoLine) {
      setValue('poLineId', '');
      setValue('itemId', '');
      return;
    }

    if (selectedPoLine !== matchedPoLine.value) {
      setValue('purchaseOrderLine', matchedPoLine.value);
    }
    setValue('poLineId', matchedPoLine.poLineId || '');
    setValue('itemId', matchedPoLine.itemId || '');
  }, [purchaseOrderLineOptions, resolvePoLineOption, selectedPoLine, setValue]);

  useEffect(() => {
    if (selectedPoLineOptions.length === 0) {
      setValue('purchaseOrderLine', '');
      setValue('poLineId', '');
      setValue('itemId', '');
      setValue('poLineSelections', []);
      return;
    }

    const first = selectedPoLineOptions[0];
    setValue('purchaseOrderLine', first.value);
    setValue('poLineId', first.poLineId || '');
    setValue('itemId', first.itemId || '');
    clearErrors('purchaseOrderLine');
    setValue(
      'poLineSelections',
      selectedPoLineOptions.map((line, index) => ({
        lineIndex: index + 1,
        poLineId: line.poLineId || '',
        poLine: line.value || '',
        itemId: line.itemId || '',
        linePayload: {
          asn: isSiteInboundManifest ? line.asn || null : undefined,
          status: line.status,
          sourcePartId: line.sourcePartId,
          purchaseType: line.purchaseType,
          description: line.description,
          quantityOrdered: line.quantityOrdered,
          quantityReceived: line.quantityReceived
        },
        // Reserved for site inbound qm
        orderedQuantity: null,
        receivedQuantity: null,
        quantityVariance: null
      }))
    );
  }, [clearErrors, isSiteInboundManifest, selectedPoLineOptions, setValue]);

  useEffect(() => {
    if (!isMultiPackageConfig) {
      setPackageConfigurations([]);
      setValue('packageConfigurations', []);
      return;
    }

    const packageCount = Math.max(0, Math.floor(normalizedPackageCount));
    setPackageConfigurations((previous) => {
      const next = Array.from({ length: packageCount }, (_, index) => {
        const packageNumber = index + 1;
        const existing = previous.find((entry) => entry.packageNumber === packageNumber);
        const defaultConfiguration = defaultPackageConfigurations.find(
          (entry) => entry.packageNumber === packageNumber
        );
        return {
          packageNumber,
          packageType:
            normalizePackageTypeToKnownOption(existing?.packageType) ||
            normalizePackageTypeToKnownOption(defaultConfiguration?.packageType) ||
            normalizePackageTypeToKnownOption(currentPackageTypes) ||
            ''
        };
      });

      setValue('packageConfigurations', next);
      return next;
    });
  }, [
    isMultiPackageConfig,
    normalizedPackageCount,
    currentPackageTypes,
    defaultPackageConfigurations,
    normalizePackageTypeToKnownOption,
    setValue
  ]);

  useEffect(() => {
    if (!requiresIdentifier || currentSsccOrAsn || currentPurchaseOrderNumber) {
      clearErrors(['ssccOrAsn', 'purchaseOrderNumber']);
    }
  }, [requiresIdentifier, currentSsccOrAsn, currentPurchaseOrderNumber, clearErrors]);

  useEffect(() => {
    if (allowsMissingIdentifier && hasSupplierOrContact) {
      clearErrors(['supplierName', 'contactName']);
    }
  }, [allowsMissingIdentifier, hasSupplierOrContact, clearErrors]);

  useEffect(() => {
    if (!shouldShowClosureSection || isSiteInboundManifest) {
      setValue('closureCode', '');
      setValue('closureNotes', '');
    }
  }, [setValue, shouldShowClosureSection, isSiteInboundManifest]);

  useEffect(() => {
    if (!selectedClosureCode) return;

    const isValidClosureCode = qmClosureCodes.some((item) => item.code === selectedClosureCode);
    if (!isValidClosureCode) {
      setValue('closureCode', '');
    }
  }, [qmClosureCodes, selectedClosureCode, setValue]);

  useEffect(() => {
    imagePreviewUrlsRef.current = attachmentPreviews
      .map((attachment) => attachment.url)
      .filter((url): url is string => !!url);
  }, [attachmentPreviews]);

  useEffect(() => {
    return () => {
      imagePreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  function onAttachmentUpload(files: FileList | File[] | null) {
    if (!files || files.length === 0) return;

    const incomingFiles = normalizeAttachmentFiles(files);
    const allowedFiles = incomingFiles.filter((file) => isSupportedAttachment(file));

    if (allowedFiles.length !== incomingFiles.length) {
      alert('Some files were skipped because the file type is not supported.', 'warning');
    }

    const validSizeFiles = allowedFiles.filter((file) => file.size <= QM_MAX_ATTACHMENT_SIZE_BYTES);
    if (validSizeFiles.length !== allowedFiles.length) {
      const maxSizeInMb = Math.round((QM_MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024)) * 10) / 10;
      alert(`Some files were skipped because they exceed ${maxSizeInMb}MB.`, 'warning');
    }

    const availableSlots = Math.max(0, QM_MAX_ATTACHMENTS - attachmentPreviews.length);
    if (availableSlots === 0) {
      alert(`Maximum ${QM_MAX_ATTACHMENTS} attachments reached.`, 'warning');
      return;
    }

    const acceptedFiles = validSizeFiles.slice(0, availableSlots);
    if (acceptedFiles.length < validSizeFiles.length) {
      alert(`Only the first ${availableSlots} file(s) were added to stay within the attachment limit.`, 'warning');
    }

    const newPreviews = acceptedFiles.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
      name: file.name,
      url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      file,
      isImage: file.type.startsWith('image/')
    }));

    if (newPreviews.length === 0) return;

    clearErrors('attachments');
    setAttachmentPreviews((previous) => {
      const combined = [...previous, ...newPreviews];
      setValue(
        'attachments',
        combined.map((preview) => preview.file)
      );
      return combined;
    });
  }

  function onAttachmentDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  function onAttachmentDragEnter(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    attachmentDragDepthRef.current += 1;
    setIsAttachmentDragActive(true);
  }

  function onAttachmentDragLeave(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    attachmentDragDepthRef.current = Math.max(0, attachmentDragDepthRef.current - 1);
    setIsAttachmentDragActive(attachmentDragDepthRef.current > 0);
  }

  function onAttachmentDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    attachmentDragDepthRef.current = 0;
    setIsAttachmentDragActive(false);
    onAttachmentUpload(event.dataTransfer.files);
  }

  function onAttachmentPaste(event: ClipboardEvent<HTMLDivElement>) {
    const clipboardFiles = Array.from(event.clipboardData.files || []);
    if (clipboardFiles.length > 0) {
      event.preventDefault();
      onAttachmentUpload(clipboardFiles);
      return;
    }

    const itemFiles = Array.from(event.clipboardData.items || [])
      .map((item) => item.getAsFile())
      .filter((file): file is File => !!file);

    if (itemFiles.length > 0) {
      event.preventDefault();
      onAttachmentUpload(itemFiles);
    }
  }

  function onDeleteAttachment(attachmentId: string) {
    setAttachmentPreviews((previous) => {
      const target = previous.find((attachment) => attachment.id === attachmentId);
      if (target?.url) {
        URL.revokeObjectURL(target.url);
      }
      if (selectedPreview?.url && target?.url && selectedPreview.url === target.url) {
        setSelectedPreview(null);
      }
      const next = previous.filter((attachment) => attachment.id !== attachmentId);
      setValue(
        'attachments',
        next.map((attachment) => attachment.file)
      );
      return next;
    });
  }

  function clearPoDependentFields() {
    setPurchaseOrderLineOptions([]);
    setSelectedPoLineOptions([]);
    setValue('supplierCode', '');
    setValue('supplierName', '');
    setValue('contactName', '');
    setValue('purchaseOrderLine', '');
    setValue('poLineId', '');
    setValue('itemId', '');
    setValue('poLineSelections', []);
  }

  async function runPoSearch(purchaseOrderValue: string) {
    setIsPoSearchLoading(true);
    const purchaseOrder = purchaseOrderValue.trim();

    try {
      if (!purchaseOrder) {
        clearPoDependentFields();
        return;
      }

      const poResponse = await fetchPODetails(purchaseOrder, '');
      if (poResponse.error || !poResponse.payload) {
        alert('No data found for the entered Purchase Order', 'warning');
        clearPoDependentFields();
        return;
      }

      const supplierCode = String(poResponse.payload.supplierId || '').trim();
      const supplier = String(poResponse.payload.supplierName || '').trim();
      const contact = String(poResponse.payload.shipToAttention || '').trim();
      setValue('supplierCode', supplierCode);
      setValue('supplierName', supplier);
      setValue('contactName', contact);
      if (supplier) {
        clearErrors('supplierName');
      }
      if (contact) {
        clearErrors('contactName');
      }

      const poLines = (poResponse.payload.lines || [])
        .map((line: ReceiptLine) => buildPoLineOption(line))
        .filter((line): line is PoLineOption => !!line);

      const dedupedPoLines = poLines.filter(
        (line, index, arr) =>
          arr.findIndex((current) => current.name === line.name && current.value === line.value) === index
      );

      if (!dedupedPoLines.length) {
        alert('No PO found for the entered Purchase Order', 'warning');
      }

      setPurchaseOrderLineOptions(dedupedPoLines);
      setSelectedPoLineOptions([]);
      setValue('purchaseOrderLine', '');
    } finally {
      setIsPoSearchLoading(false);
    }
  }

  async function onSearchPurchaseOrder() {
    await runPoSearch(purchaseOrderInput);
  }

  function prefillPackageFieldsFromSscc(sscc: SSCCProps, asnValue?: string) {
    const normalizedAsn = String(asnValue || '')
      .trim()
      .toUpperCase();

    const packages = (sscc.receiptedEvents || [])
      .flatMap((event) => event.packages || [])
      .filter((pkg) => {
        if (!normalizedAsn) return true;
        return (
          String(pkg.asn || '')
            .trim()
            .toUpperCase() === normalizedAsn
        );
      });

    const packageCount = packages.reduce((total, pkg) => total + Number(pkg.quantity || 0), 0);
    const packageTypes = Array.from(
      new Set(packages.map((pkg) => String(pkg.packageType || '').trim()).filter((pkgType) => !!pkgType))
    ).join(', ');

    if (packageCount > 0) {
      setValue('numberOfLabels', packageCount);
    }

    if (packageTypes) {
      setValue('packageTypes', packageTypes);
    }
  }

  async function onSearchSsccOrAsn() {
    const lookup = ssccOrAsnInput.trim();
    if (!lookup) {
      setError('ssccOrAsn', { type: 'manual', message: 'Enter SSCC/ASN to search' });
      return;
    }

    const isAsn = /^MRL[A-Za-z0-9]{8}$/i.test(lookup);
    const isSscc = /^[A-Za-z0-9]{15}$/.test(lookup);

    if (!isAsn && !isSscc) {
      setError('ssccOrAsn', {
        type: 'manual',
        message: 'Invalid format. Use SSCC (15 chars) or ASN (MRL + 8 characters)'
      });
      return;
    }

    let resolvedPo = '';

    if (isSscc) {
      const matchedSscc = ssccs.find((sscc) => sscc.id.toLowerCase() === lookup.toLowerCase());
      if (matchedSscc) {
        prefillPackageFieldsFromSscc(matchedSscc);
      }
      resolvedPo =
        matchedSscc?.receiptedEvents
          ?.flatMap((event) => event.packages || [])
          .map((pkg) => String(pkg.po || '').trim())
          .find((po) => !!po) || '';
    }

    if (isAsn) {
      const matched = ssccs.find((sscc) =>
        (sscc.receiptedEvents || []).some((event) =>
          (event.packages || []).some(
            (pkg) =>
              String(pkg.asn || '')
                .trim()
                .toUpperCase() === lookup.toUpperCase()
          )
        )
      );

      if (matched) {
        prefillPackageFieldsFromSscc(matched, lookup);
        resolvedPo =
          matched.receiptedEvents
            ?.flatMap((event) => event.packages || [])
            .filter(
              (pkg) =>
                String(pkg.asn || '')
                  .trim()
                  .toUpperCase() === lookup.toUpperCase()
            )
            .map((pkg) => String(pkg.po || '').trim())
            .find((po) => !!po) || '';
      }
    }

    if (!resolvedPo) {
      setError('ssccOrAsn', { type: 'manual', message: 'No data found for the entered SSCC/ASN' });
      alert('No data found for the entered SSCC/ASN', 'warning');
      setPurchaseOrderInput('');
      setValue('purchaseOrderNumber', '');
      clearPoDependentFields();
      return;
    }

    clearErrors('ssccOrAsn');
    setPurchaseOrderInput(resolvedPo);
    setValue('purchaseOrderNumber', resolvedPo);
    await runPoSearch(resolvedPo);
  }

  async function onSubmitForm(data: RaiseQMNewModel) {
    if (isSubmitInProgressRef.current || isSubmitInProgress) return;
    isSubmitInProgressRef.current = true;
    setIsSubmitInProgress(true);
    setHasAttemptedSubmit(true);
    clearErrors();

    applyDynamicRequiredErrors();

    if (missingRequiredItems.length > 0) {
      isSubmitInProgressRef.current = false;
      setIsSubmitInProgress(false);
      return;
    }

    data.type = type;
    const normalizedPoLine = String(data.purchaseOrderLine || '').trim();
    const selectedPoLineOption = resolvePoLineOption(normalizedPoLine, data.poLineId);

    if (selectedPoLineOptions.length > 0) {
      const first = selectedPoLineOptions[0];
      data.purchaseOrderLine = first.value;
      data.poLineId = first.poLineId;
      data.itemId = first.itemId;
      data.poLineSelections = selectedPoLineOptions.map((line, index) => ({
        lineIndex: index + 1,
        poLineId: line.poLineId,
        poLine: line.value,
        itemId: line.itemId,
        linePayload: {
          asn: isSiteInboundManifest ? line.asn || null : undefined,
          status: line.status,
          sourcePartId: line.sourcePartId,
          purchaseType: line.purchaseType,
          description: line.description,
          quantityOrdered: line.quantityOrdered,
          quantityReceived: line.quantityReceived
        },
        // Reserved for site inbound qm
        orderedQuantity: null,
        receivedQuantity: null,
        quantityVariance: null
      }));
    } else {
      data.purchaseOrderLine = selectedPoLineOption?.value || normalizedPoLine;
      data.poLineId = selectedPoLineOption?.poLineId || String(data.poLineId || '').trim();
      data.itemId = selectedPoLineOption?.itemId || String(data.itemId || '').trim();
      data.poLineSelections = [];
    }

    if (isMultiPackageConfig) {
      data.packageConfigurations = packageConfigurations.map((pkg) => ({
        packageNumber: pkg.packageNumber,
        packageType: String(pkg.packageType || '').trim()
      }));
      data.packageTypes = data.packageConfigurations[0]?.packageType || '';
    } else {
      data.packageConfigurations = [];
    }
    data.reason = qmReasons.find((r) => r.reasonId === data.reason.reasonId) || { id: '', reasonId: '', name: '' };
    data.issue = qmIssues.find((i) => i.issueId === data.issue.issueId) || { id: '', issueId: '', name: '' };

    if (!shouldShowClosureSection || isSiteInboundManifest || !data.closureCode) {
      data.closureCode = '';
      data.closureNotes = '';
    }

    if (isSiteInboundManifest) {
      const selectedLocation = qmLocations.find((location) => location.value === data.locationName);
      const [, fallbackLocationCode, fallbackLocationId] = String(data.locationName || '').split(':');
      if (selectedLocation) {
        data.locationName = selectedLocation.code;
        data.itemLocationId = selectedLocation.itemLocationId ?? null;
        data.itemLocationLabel = selectedLocation.label;
      } else if (fallbackLocationCode && fallbackLocationId && Number.isInteger(Number(fallbackLocationId))) {
        data.locationName = fallbackLocationCode;
        data.itemLocationId = Number(fallbackLocationId);
        data.itemLocationLabel = fallbackLocationCode;
      }
      if (data.locationName && !data.itemLocationId) {
        setError('locationName', { type: 'manual', message: 'Select Item Location' });
        isSubmitInProgressRef.current = false;
        setIsSubmitInProgress(false);
        return;
      }
      if (!data.locationName && isItemLocationRequired) {
        setError('locationName', { type: 'manual', message: 'Select Item Location' });
        isSubmitInProgressRef.current = false;
        setIsSubmitInProgress(false);
        return;
      }
      if (!data.locationName && !isItemLocationRequired) {
        data.locationName = '';
        data.itemLocationId = null;
        data.itemLocationLabel = null;
      }
    }

    try {
      await onSubmit(data);
    } finally {
      isSubmitInProgressRef.current = false;
      setIsSubmitInProgress(false);
    }
  }

  function applyDynamicRequiredErrors() {
    if (missingRequiredItems.includes('Reason For QM')) {
      setError('reason.reasonId', { type: 'manual', message: 'Select Reason for QM' });
    }
    if (missingRequiredItems.includes('SSCC / ASN or Purchase Order')) {
      setError('ssccOrAsn', { type: 'manual', message: 'Provide SSCC/ASN or Purchase Order' });
      setError('purchaseOrderNumber', { type: 'manual', message: 'Provide Purchase Order or SSCC/ASN' });
    }
    if (missingRequiredItems.includes('SSCC / ASN')) {
      setError('ssccOrAsn', { type: 'manual', message: 'Provide SSCC/ASN' });
    }
    if (missingRequiredItems.includes('Supplier or Contact')) {
      setError('supplierName', { type: 'manual', message: 'Provide Supplier or Contact' });
      setError('contactName', { type: 'manual', message: 'Provide Supplier or Contact' });
    }
    if (missingRequiredItems.includes('Number of Packages')) {
      setError('numberOfLabels', { type: 'manual', message: 'Enter Number of Packages' });
    }
    if (missingRequiredItems.includes('Package Types')) {
      setError('packageTypes', { type: 'manual', message: 'Provide Package Types' });
    }
    if (missingRequiredItems.includes('Item Location')) {
      setError('locationName', { type: 'manual', message: 'Select Item Location' });
    }
    if (missingRequiredItems.includes('Closure Code')) {
      setError('closureCode', { type: 'manual', message: 'Select Closure Code' });
    }
    if (missingRequiredItems.includes('File Upload')) {
      setError('attachments', { type: 'manual', message: 'Add at least one file' });
    }
    if (missingRequiredItems.includes('ASN Line')) {
      setError('purchaseOrderLine', { type: 'manual', message: 'Select at least one ASN line' });
    }
  }

  function onInvalidSubmit() {
    setHasAttemptedSubmit(true);
    applyDynamicRequiredErrors();
  }

  function onPackageTypeChange(packageNumber: number, packageType: string) {
    setPackageConfigurations((previous) => {
      const next = previous.map((pkg) => (pkg.packageNumber === packageNumber ? { ...pkg, packageType } : pkg));
      setValue('packageConfigurations', next);
      return next;
    });
  }

  return (
    <>
      <Dialog
        open={open}
        maxWidth="lg"
        fullWidth
        fullScreen={isSmallScreen}
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, md: 2 },
            overflow: 'hidden',
            maxHeight: { xs: '100dvh', md: '92dvh' },
            bgcolor: 'background.default',
            backgroundImage: 'none',
            display: 'flex'
          }
        }}>
        <form
          onSubmit={handleSubmit(onSubmitForm, onInvalidSubmit)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            height: '100%'
          }}>
          <Stack
            spacing={2}
            sx={{
              minHeight: 0,
              flex: 1,
              overflow: 'hidden',
              p: { xs: 1.25, md: '1rem 2rem' },
              '& .MuiTypography-root': { fontSize: { xs: '0.84rem', md: '0.88rem' } },
              '& .MuiButton-root': { fontSize: { xs: '0.76rem', md: '0.8rem' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.84rem', md: '0.88rem' } },
              '& .MuiFormHelperText-root': { fontSize: '0.68rem', lineHeight: 1.2 }
            }}>
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: { xs: 0, md: 0.25 } }}>
              <Stack spacing={1} sx={{ pt: 0.25 }}>
                <Box
                  sx={{
                    px: { xs: 1, md: 1.5 },
                    py: { xs: 1, md: 1.25 },
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                  }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.25}>
                    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        <Typography
                          variant="h2"
                          fontWeight={700}
                          sx={{
                            lineHeight: 1.1,
                            mr: 0.5,
                            fontSize: { xs: '0.98rem !important', md: '1.12rem !important' }
                          }}>
                          Raise a QM
                        </Typography>
                        <Chip size="small" variant="outlined" label={type.replace('_', ' ')} />
                      </Stack>
                    </Stack>
                    <IconButton
                      onClick={onClose}
                      sx={{
                        mt: -0.25,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'grey.100' }
                      }}>
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                </Box>
              </Stack>
              <Divider sx={{ opacity: 0.7 }} />
              <Stack spacing={2}>
                <Box sx={sectionCardSx}>
                  <Typography sx={sectionTitleSx}>Identification</Typography>
                  {hasSelectedReason ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1, fontSize: '0.64rem' }}>
                      <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
                        *
                      </Box>{' '}
                      Required field.
                      {requiresIdentifier
                        ? isSiteInboundManifest
                          ? ' SSCC / ASN is linked from the manifest.'
                          : ' Provide either SSCC / ASN or Purchase Order.'
                        : ''}
                    </Typography>
                  ) : null}
                  {isSiteInboundManifest && (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
                      <TextField
                        label="Trip Reference"
                        size="small"
                        value={siteInboundManifestContext?.tripReference || ''}
                        InputProps={{ readOnly: true }}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Manifest ID"
                        size="small"
                        value={siteInboundManifestContext?.manifestId || ''}
                        InputProps={{ readOnly: true }}
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                  )}
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      {renderFieldLabel('Reason For QM', true)}
                      {isReasonOptionsLoading ? (
                        <Button variant="outlined" disabled sx={{ justifyContent: 'flex-start', minHeight: '40px' }}>
                          Loading reason options...
                        </Button>
                      ) : (
                        <Box sx={invalidOutlineSx(!!errors.reason?.reasonId)}>
                          <AutocompleteFormInput
                            sx={{ width: '100%' }}
                            name="reason.reasonId"
                            required
                            placeholder="Search or select..."
                            control={control}
                            error={errors.reason?.reasonId as any}
                            options={qmReasons.map((type) => ({ name: type.name, value: type.reasonId }))}
                          />
                        </Box>
                      )}
                    </Stack>
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      {renderFieldLabel('SSCC / ASN', isSsccOrAsnRequired)}
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ ...invalidOutlineSx(!!errors['ssccOrAsn']), flex: 1 }}>
                          <TextFieldFormInput
                            sx={{ width: '100%' }}
                            name="ssccOrAsn"
                            control={control}
                            error={errors['ssccOrAsn']}
                            onChange={(value) => setSsccOrAsnInput(value)}
                          />
                        </Box>
                        {!isSiteInboundManifest && (
                          <Button variant="outlined" onClick={onSearchSsccOrAsn}>
                            Search
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                    {!isSiteInboundManifest && (
                      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        {renderFieldLabel('Purchase Order', isPurchaseOrderRequired)}
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ ...invalidOutlineSx(!!errors['purchaseOrderNumber']), flex: 1 }}>
                            <TextFieldFormInput
                              sx={{ width: '100%' }}
                              name="purchaseOrderNumber"
                              control={control}
                              error={errors['purchaseOrderNumber']}
                              onChange={(value) => {
                                setPurchaseOrderInput(value);
                                clearPoDependentFields();
                              }}
                            />
                          </Box>
                          <Button
                            variant="outlined"
                            onClick={onSearchPurchaseOrder}
                            disabled={isPoSearchLoading}
                            sx={{ minWidth: 88 }}>
                            {isPoSearchLoading ? <CircularProgress size={16} /> : 'Search'}
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                  </Stack>
                </Box>

                <Box sx={sectionCardSx}>
                  <Typography sx={sectionTitleSx}>
                    {isSiteInboundManifest ? 'ASN Details' : 'Purchase Order Details'}
                  </Typography>
                  <Stack spacing={1} sx={{ width: '100%' }}>
                    {isSiteInboundManifest && siteInboundManifestContext?.asnDetails?.length ? (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {siteInboundManifestContext.asnDetails.map((asnDetail) => {
                          const normalizedAsn = String(asnDetail.asn || '').trim();
                          const isSelected = selectedSiteInboundAsns.some(
                            (selectedAsn) => selectedAsn.trim().toUpperCase() === normalizedAsn.toUpperCase()
                          );
                          const canToggleAsn = shouldDefaultSelectSiteInboundAsnLines && normalizedAsn;

                          if (!canToggleAsn) {
                            return (
                              <Chip
                                key={asnDetail.asn}
                                label={`${asnDetail.asn} (${asnDetail.lines.length} line${asnDetail.lines.length === 1 ? '' : 's'})`}
                                variant="outlined"
                                size="small"
                              />
                            );
                          }

                          return (
                            <Box
                              key={asnDetail.asn}
                              component="button"
                              type="button"
                              onClick={() =>
                                setSelectedSiteInboundAsns((previous) =>
                                  isSelected
                                    ? previous.filter(
                                        (selectedAsn) =>
                                          selectedAsn.trim().toUpperCase() !== normalizedAsn.toUpperCase()
                                      )
                                    : [...previous, normalizedAsn]
                                )
                              }
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                minHeight: 30,
                                px: 1,
                                py: 0.35,
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: isSelected ? alpha(theme.palette.primary.main, 0.7) : 'divider',
                                bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.09) : 'background.paper',
                                color: isSelected ? 'primary.dark' : 'text.primary',
                                cursor: 'pointer',
                                font: 'inherit',
                                boxShadow: isSelected
                                  ? `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.18)}`
                                  : 'none',
                                transition: theme.transitions.create(
                                  ['background-color', 'border-color', 'box-shadow'],
                                  {
                                    duration: theme.transitions.duration.shortest
                                  }
                                ),
                                '&:hover': {
                                  borderColor: isSelected ? 'primary.main' : alpha(theme.palette.primary.main, 0.45),
                                  bgcolor: isSelected
                                    ? alpha(theme.palette.primary.main, 0.13)
                                    : alpha(theme.palette.primary.main, 0.04)
                                },
                                '&:focus-visible': {
                                  outline: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                                  outlineOffset: 2
                                }
                              }}>
                              <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 700, lineHeight: 1 }}>
                                {asnDetail.asn}
                              </Typography>
                              <Typography
                                component="span"
                                sx={{ fontSize: '0.7rem', lineHeight: 1, color: 'text.secondary' }}>
                                {asnDetail.lines.length} line{asnDetail.lines.length === 1 ? '' : 's'}
                              </Typography>
                              {isSelected ? (
                                <Box
                                  component="span"
                                  sx={{
                                    px: 0.55,
                                    py: 0.15,
                                    borderRadius: '6px',
                                    bgcolor: alpha(theme.palette.primary.main, 0.14),
                                    color: 'primary.dark',
                                    fontSize: '0.62rem',
                                    fontWeight: 700,
                                    lineHeight: 1.3
                                  }}>
                                  Selected
                                </Box>
                              ) : null}
                            </Box>
                          );
                        })}
                      </Stack>
                    ) : null}
                    {renderFieldLabel(
                      isSiteInboundManifest ? 'ASN Lines' : 'Purchase Order Line',
                      isSiteInboundAsnLineRequired
                    )}
                    {isSiteInboundManifest && shouldLockSiteInboundAsnLines && siteInboundAsnLineOptions.length > 0 ? (
                      <Stack spacing={0.75}>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {siteInboundAsnLineOptions.map((option) => (
                            <Chip
                              key={`${option.poLineId}-${option.itemId}-${option.name}`}
                              size="small"
                              label={option.name}
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          All ASN lines in this SSCC will be included.
                        </Typography>
                      </Stack>
                    ) : isSiteInboundManifest && shouldDefaultSelectSiteInboundAsnLines ? (
                      <Stack spacing={0.75}>
                        {selectedPoLineOptions.length > 0 ? (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {selectedPoLineOptions.map((option) => (
                              <Chip
                                key={`${option.asn}-${option.poLineId}-${option.itemId}-${option.name}`}
                                size="small"
                                label={option.name}
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Select at least one ASN in ASN Details.
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          ASN lines are included from the SSCC.
                        </Typography>
                      </Stack>
                    ) : isSiteInboundManifest && siteInboundAsnLineOptions.length > 0 ? (
                      <Stack spacing={0.75} sx={invalidOutlineSx(!!errors['purchaseOrderLine'])}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setSelectedPoLineOptions(siteInboundAsnLineOptions)}>
                            Select All
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => setSelectedPoLineOptions([])}>
                            Clear
                          </Button>
                        </Stack>
                        <Autocomplete
                          multiple
                          options={siteInboundAsnLineOptions}
                          value={selectedPoLineOptions}
                          onChange={(_, nextValue) => setSelectedPoLineOptions(nextValue)}
                          disableCloseOnSelect
                          getOptionLabel={(option) => option.name}
                          isOptionEqualToValue={(option, value) =>
                            option.poLineId === value.poLineId && option.itemId === value.itemId
                          }
                          renderInput={(params) => (
                            <TextField {...params} size="small" placeholder="Select ASN lines" />
                          )}
                        />
                      </Stack>
                    ) : purchaseOrderInput.trim() && purchaseOrderLineOptions.length > 0 ? (
                      <Stack spacing={0.75}>
                        <Autocomplete
                          multiple
                          options={purchaseOrderLineOptions}
                          value={selectedPoLineOptions}
                          onChange={(_, nextValue) => {
                            setSelectedPoLineOptions(nextValue);
                          }}
                          disableCloseOnSelect
                          getOptionDisabled={(option) =>
                            selectedPoLineOptions.some((selected) => selected.poLineId === option.poLineId)
                          }
                          getOptionLabel={(option) => option.value}
                          isOptionEqualToValue={(option, value) => option.poLineId === value.poLineId}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="Select none, one, or multiple PO lines"
                              error={!!errors['purchaseOrderLine']}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box sx={{ width: '100%', py: 0.5 }}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                  sx={{ mb: 0.75 }}>
                                  <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                                    {option.name}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} sx={{ ml: 1, flexShrink: 0 }}>
                                    {option.status && (
                                      <Chip
                                        size="small"
                                        label={option.status}
                                        color="info"
                                        variant="filled"
                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                      />
                                    )}
                                    {option.purchaseType && (
                                      <Chip
                                        size="small"
                                        label={option.purchaseType}
                                        color="default"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                      />
                                    )}
                                  </Stack>
                                </Stack>
                                {option.sourcePartId && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.25 }}>
                                    Part: <strong>{option.sourcePartId}</strong>
                                  </Typography>
                                )}
                                {option.description && (
                                  <Typography
                                    variant="body2"
                                    color="text.primary"
                                    sx={{
                                      mb: 0.5,
                                      fontSize: '0.85rem',
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    }}>
                                    {option.description}
                                  </Typography>
                                )}
                                <Stack direction="row" spacing={2}>
                                  <Typography variant="caption" color="text.secondary">
                                    Ordered: <strong>{option.quantityOrdered ?? '—'}</strong>
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Received: <strong>{option.quantityReceived ?? '—'}</strong>
                                  </Typography>
                                </Stack>
                              </Box>
                            </li>
                          )}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Optional. If multiple are selected, the first selection is mirrored to the main QM fields.
                        </Typography>
                      </Stack>
                    ) : (
                      <TextFieldFormInput
                        sx={{ width: '100%' }}
                        name="purchaseOrderLine"
                        control={control}
                        error={errors['purchaseOrderLine']}
                      />
                    )}
                  </Stack>
                </Box>

                <Box sx={sectionCardSx}>
                  <Typography sx={sectionTitleSx}>Contact and Package</Typography>
                  {requiresSupplierOrContact ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Provide either Supplier or Contact.
                    </Typography>
                  ) : null}
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                    {!isSiteInboundManifest && (
                      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        {renderFieldLabel('Supplier', isSupplierRequired)}
                        <Box sx={invalidOutlineSx(!!errors['supplierName'])}>
                          <TextFieldFormInput
                            sx={{ width: '100%' }}
                            name="supplierName"
                            control={control}
                            required={isSupplierRequired}
                            error={errors['supplierName']}
                          />
                        </Box>
                      </Stack>
                    )}
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      {renderFieldLabel('Contact', isContactRequired)}
                      <Box sx={invalidOutlineSx(!!errors['contactName'])}>
                        <TextFieldFormInput
                          sx={{ width: '100%' }}
                          name="contactName"
                          control={control}
                          required={isContactRequired}
                          error={errors['contactName']}
                        />
                      </Box>
                    </Stack>
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={fieldLabelSx}>Additional Details</Typography>
                      <TextFieldFormInput
                        sx={{ width: '100%' }}
                        name="other"
                        control={control}
                        error={errors['other']}
                      />
                    </Stack>
                  </Stack>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    justifyContent="space-between"
                    sx={{ mt: 1.5 }}>
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      <Stack spacing={0.25}>
                        {renderFieldLabel('Item Location', isItemLocationRequired)}
                        {isSiteInboundManifest && siteInboundWarehouseDisplay ? (
                          <Typography variant="caption" color="text.secondary">
                            Warehouse: {siteInboundWarehouseDisplay}
                          </Typography>
                        ) : null}
                      </Stack>
                      {isLocationOptionsLoading ? (
                        <Button variant="outlined" disabled sx={{ justifyContent: 'flex-start', minHeight: '40px' }}>
                          Loading location options...
                        </Button>
                      ) : isSiteInboundManifest && qmLocations.length === 0 ? (
                        <Button variant="outlined" disabled sx={{ justifyContent: 'flex-start', minHeight: '40px' }}>
                          {siteInboundReceivingWarehouseIds.length > 0
                            ? 'No item locations configured'
                            : 'No item location available'}
                        </Button>
                      ) : (
                        <Box sx={invalidOutlineSx(!!errors['locationName'])}>
                          <AutocompleteFormInput
                            sx={{ width: '100%' }}
                            name="locationName"
                            required={isItemLocationRequired}
                            placeholder="Search or select..."
                            control={control}
                            error={errors['locationName']}
                            options={qmLocations.map((location) => ({
                              name: location.label,
                              value: location.value
                            }))}
                          />
                        </Box>
                      )}
                    </Stack>
                    {isSiteInboundManifest ? (
                      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={fieldLabelSx}>Print Labels</Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={currentPrintLabels}
                              onChange={(event) => setValue('printLabels', event.target.checked)}
                            />
                          }
                          label="Print Labels"
                          sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.78rem' } }}
                        />
                      </Stack>
                    ) : null}
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      {renderFieldLabel('Number of Packages', !isSiteInboundOptionalPackageReason)}
                      <Box sx={invalidOutlineSx(!!errors['numberOfLabels'])}>
                        <PackageCountStepper control={control} />
                      </Box>
                    </Stack>
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      {renderFieldLabel('Package Types', !isSiteInboundOptionalPackageReason)}
                      {!isMultiPackageConfig ? (
                        <Box sx={invalidOutlineSx(!!errors['packageTypes'])}>
                          <AutocompleteFormInput
                            sx={{ width: '100%' }}
                            name="packageTypes"
                            control={control}
                            required
                            placeholder="Search or select..."
                            options={packageTypeSelectOptions.map((type) => ({
                              name: type,
                              value: type
                            }))}
                            error={errors['packageTypes']}
                          />
                        </Box>
                      ) : (
                        <Stack spacing={0.75} sx={invalidOutlineSx(!!errors['packageTypes'])}>
                          {packageConfigurations.map((pkg) => (
                            <Stack
                              key={pkg.packageNumber}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ width: '100%' }}>
                              <Chip
                                label={`Pkg ${pkg.packageNumber}`}
                                size="small"
                                variant="outlined"
                                sx={{ minWidth: 74, fontSize: '0.7rem' }}
                              />
                              <Autocomplete
                                sx={{ flex: 1 }}
                                size="small"
                                freeSolo
                                options={packageTypeSelectOptions}
                                value={pkg.packageType}
                                onChange={(_, newValue) => {
                                  onPackageTypeChange(
                                    pkg.packageNumber,
                                    normalizePackageTypeToKnownOption(String(newValue || '').trim())
                                  );
                                }}
                                onInputChange={(_, newInputValue) => {
                                  onPackageTypeChange(
                                    pkg.packageNumber,
                                    normalizePackageTypeToKnownOption(String(newInputValue || '').trim())
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} size="small" placeholder="Select package type" />
                                )}
                              />
                            </Stack>
                          ))}
                          <Typography variant="caption" color="text.secondary">
                            Configure package type for each package row.
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </Box>

                <Box sx={sectionCardSx}>
                  <Typography sx={sectionTitleSx}>Description and Attachments</Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={fieldLabelSx}>Description / Comments</Typography>
                      <TextFieldFormInput
                        sx={{
                          '& .MuiInputBase-inputMultiline': {
                            p: '0.45em 0.65em'
                          }
                        }}
                        name="description"
                        control={control}
                        error={errors['description']}
                        multiline
                        rows={3}
                      />
                    </Stack>
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        {renderFieldLabel('File Upload', isAttachmentRequired)}
                        <Typography variant="caption" color="text.secondary">
                          {attachmentPreviews.length}/{QM_MAX_ATTACHMENTS} selected
                        </Typography>
                      </Stack>
                      <Box
                        onDragEnter={onAttachmentDragEnter}
                        onDragOver={onAttachmentDragOver}
                        onDragLeave={onAttachmentDragLeave}
                        onDrop={onAttachmentDrop}
                        onPaste={onAttachmentPaste}
                        tabIndex={0}
                        sx={{
                          border: '2px dashed',
                          borderColor: isAttachmentDragActive ? 'primary.main' : 'primary.light',
                          borderRadius: 2,
                          p: 1.5,
                          minHeight: 104,
                          bgcolor: isAttachmentDragActive
                            ? alpha(theme.palette.primary.main, 0.12)
                            : alpha(theme.palette.primary.main, 0.04),
                          boxShadow: isAttachmentDragActive
                            ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.24)}, 0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`
                            : `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transition: 'all 160ms ease',
                          outline: 'none',
                          '&:focus-visible': {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.28)}`
                          }
                        }}>
                        <Stack spacing={0.75} alignItems="flex-start">
                          <Button variant="outlined" component="label">
                            Add File
                            <input
                              hidden
                              accept={ATTACHMENT_ACCEPT_TYPES.join(',')}
                              type="file"
                              multiple
                              onChange={(e) => onAttachmentUpload(e.target.files)}
                            />
                          </Button>
                          <Typography
                            variant="caption"
                            color={isAttachmentDragActive ? 'primary.main' : 'text.secondary'}>
                            Drag and drop files here, or click here first then paste a screenshot with Ctrl+V.
                          </Typography>
                        </Stack>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Allowed: images, PDF, DOCX/DOC, CSV, XLSX/XLS, MSG. Max {QM_MAX_ATTACHMENTS} files, up to{' '}
                        {Math.round((QM_MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024)) * 10) / 10}MB each.
                      </Typography>
                      {errors['attachments'] && attachmentPreviews.length === 0 ? (
                        <Typography variant="caption" color="error">
                          {String(errors['attachments']?.message || 'Add at least one file')}
                        </Typography>
                      ) : null}
                      {attachmentPreviews.length > 0 && (
                        <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', py: 1 }}>
                          {attachmentPreviews.map((attachment) => (
                            <Stack
                              key={attachment.id}
                              spacing={0.5}
                              sx={{
                                minWidth: 110,
                                maxWidth: 130,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                overflow: 'hidden',
                                bgcolor: 'background.paper',
                                flexShrink: 0
                              }}>
                              <Box
                                sx={{ position: 'relative', cursor: attachment.isImage ? 'zoom-in' : 'default' }}
                                onClick={() => {
                                  if (attachment.isImage && attachment.url) {
                                    setSelectedPreview({ name: attachment.name, url: attachment.url });
                                  }
                                }}>
                                {attachment.isImage && attachment.url ? (
                                  <Box
                                    component="img"
                                    src={attachment.url}
                                    alt={attachment.name}
                                    sx={{
                                      width: '100%',
                                      height: 90,
                                      objectFit: 'cover',
                                      display: 'block'
                                    }}
                                  />
                                ) : (
                                  <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{ width: '100%', height: 90, bgcolor: 'grey.100', color: 'text.secondary' }}>
                                    <InsertDriveFileIcon sx={{ fontSize: 24 }} />
                                  </Stack>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onDeleteAttachment(attachment.id);
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    bgcolor: 'rgba(0,0,0,0.55)',
                                    color: 'common.white',
                                    p: 0.25,
                                    '&:hover': { bgcolor: 'error.main' }
                                  }}>
                                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                              <Stack sx={{ px: 0.75, pb: 0.75 }} spacing={0.25}>
                                <Typography
                                  variant="caption"
                                  noWrap
                                  title={attachment.name}
                                  sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.7rem' }}>
                                  {attachment.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'primary.main', fontSize: '0.5rem', fontWeight: 600 }}>
                                  {formatBytes(attachment.file.size)}
                                </Typography>
                              </Stack>
                            </Stack>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </Box>

                {shouldShowClosureSection && !isSiteInboundManifest && (
                  <Box sx={sectionCardSx}>
                    <Typography sx={sectionTitleSx}>Closure</Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography>
                          {isClosureSelectionRequired
                            ? 'Closure required for this reason'
                            : 'Optional closure for this reason'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {isClosureSelectionRequired
                            ? 'Select a closure code to close this QM at creation time.'
                            : 'Leave closure blank to send this QM to the coordinator, or select a closure code to close it now.'}
                        </Typography>
                      </Stack>
                      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        {renderFieldLabel('Closure Code', isClosureSelectionRequired && hasClosureOptionsForReason)}
                        {isClosureOptionsLoading ? (
                          <Button variant="outlined" disabled sx={{ justifyContent: 'flex-start', minHeight: '40px' }}>
                            Loading closure options...
                          </Button>
                        ) : (
                          <Box sx={invalidOutlineSx(!!errors['closureCode'])}>
                            <AutocompleteFormInput
                              sx={{ width: '100%' }}
                              name="closureCode"
                              control={control}
                              required={isClosureSelectionRequired && hasClosureOptionsForReason}
                              placeholder="Search or select..."
                              error={errors['closureCode'] as any}
                              options={qmClosureCodes.map((item) => ({
                                name: item.label,
                                value: item.code
                              }))}
                            />
                          </Box>
                        )}
                        {!isClosureOptionsLoading && !hasClosureOptionsForReason && (
                          <Typography variant="caption" color="text.secondary">
                            No closure codes configured for the selected reason.
                          </Typography>
                        )}
                      </Stack>
                      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={fieldLabelSx}>Closure Notes</Typography>
                        <TextFieldFormInput
                          sx={{ width: '100%' }}
                          name="closureNotes"
                          control={control}
                          error={errors['closureNotes'] as any}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                )}
              </Stack>
              {hasAttemptedSubmit && missingRequiredItems.length > 0 && (
                <Alert severity="warning">Please complete the highlighted fields.</Alert>
              )}
            </Stack>
            <Divider />
            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={1.25}
              justifyContent="flex-end"
              sx={{
                pb: { xs: 0.5, md: 0.25 },
                pt: 0.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default'
              }}>
              <Button variant="text" onClick={onClose} sx={{ minWidth: { sm: 100 } }}>
                Cancel
              </Button>
              <StyledButton
                variant="contained"
                type="submit"
                disabled={user.isReadOnly || isSubmitInProgress}
                startIcon={isSubmitInProgress ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{ minWidth: { sm: 140 } }}>
                {isSubmitInProgress ? 'Raising QM...' : 'Raise QM'}
              </StyledButton>
            </Stack>
          </Stack>
        </form>
      </Dialog>
      <Dialog
        open={!!selectedPreview}
        onClose={() => setSelectedPreview(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.default'
          }
        }}>
        <Stack spacing={1} sx={{ p: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" noWrap title={selectedPreview?.name || ''}>
              {selectedPreview?.name || ''}
            </Typography>
            <IconButton onClick={() => setSelectedPreview(null)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          {selectedPreview && (
            <Box
              component="img"
              src={selectedPreview.url}
              alt={selectedPreview.name}
              sx={{
                width: '100%',
                maxHeight: { xs: '70vh', md: '78vh' },
                objectFit: 'contain',
                borderRadius: 1
              }}
            />
          )}
        </Stack>
      </Dialog>
    </>
  );
}
